/**
 * EzERP API Service
 * Handles all backend API calls with authentication
 */

export const API_BASE = process.env.REACT_APP_BACKEND_URL || (
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? `http://${window.location.hostname}:8001`
    : ''
);

// Token management
let authToken = localStorage.getItem('ezerpToken');
let currentUser = JSON.parse(localStorage.getItem('ezerpUser') || 'null');

export const setAuthToken = (token, user) => {
  authToken = token;
  currentUser = user;
  if (token) {
    localStorage.setItem('ezerpToken', token);
    localStorage.setItem('ezerpUser', JSON.stringify(user));
  } else {
    localStorage.removeItem('ezerpToken');
    localStorage.removeItem('ezerpUser');
  }
};

export const getAuthToken = () => authToken;
export const getCurrentUser = () => currentUser;
export const isAuthenticated = () => !!authToken;

export const logout = () => {
  const tokenToClose = authToken;
  if (tokenToClose) {
    fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${tokenToClose}` },
      keepalive: true,
    }).catch(() => {});
  }
  setAuthToken(null, null);
  window.location.href = '/login';
};

// API request helper
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  // Phase 2: Context Visibility Headers
  const tenantId = localStorage.getItem('ezerp_tenant_id');
  const orgId = localStorage.getItem('ezerp_org_id');
  
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  if (orgId) headers['X-Org-Scope-ID'] = orgId;

  console.log(`[API] Fetching: ${url}`, { method: options.method || 'GET' });
  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });
    console.log(`[API] Response from ${url}:`, response.status);

    if (response.status === 401) {
      console.warn('[API] 401 Unauthorized, logging out...');
      logout();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      if (response.status === 403) {
        // Forbidden - might be a context mismatch or unauthorized scope
        const error = await response.json().catch(() => ({ detail: 'Access denied: Unauthorized context or scope' }));
        throw new Error(error.detail || 'Access denied');
      }
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error);
    throw error;
  }
};

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    console.log(`[API] POST login to: ${API_BASE}/api/auth/login`);
    const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formData,
    });
    console.log(`[API] Login response status:`, response.status);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }));
      console.error('[API] Login failed body:', error);
      throw new Error(error.detail || 'Invalid credentials');
    }

    const data = await response.json();
    console.log('[API] Login successful, token received');
    setAuthToken(data.access_token, data.user);
    return data;
  },

  register: async (userData) => {
    return apiRequest('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  forgotPassword: async (email) => {
    const response = await fetch(`${API_BASE}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Password reset request failed' }));
      throw new Error(error.detail || 'Password reset request failed');
    }

    return response.json();
  },

  resetPassword: async (token, newPassword) => {
    const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Password reset failed' }));
      throw new Error(error.detail || 'Password reset failed');
    }

    return response.json();
  },

  getMe: async () => {
    const user = await apiRequest('/api/auth/me');
    if (user && authToken) {
      setAuthToken(authToken, user);
    }
    return user;
  },
};

export const presenceAPI = {
  heartbeat: async (payload) => apiRequest('/api/presence/heartbeat', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
};

// Presentation Hub API
export const presentationHubAPI = {
  getStatus: async (includePublic = false) => {
    const params = includePublic ? '?include_public=true' : '';
    return apiRequest(`/api/presentation-hub/status${params}`);
  },
  getDeckTranslations: async (deckId) => {
    return apiRequest(`/api/presentation-hub/decks/${deckId}/translations`);
  },
  saveDeckTranslations: async (deckId, payload) => {
    return apiRequest(`/api/presentation-hub/decks/${deckId}/translations`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  publishDeckTranslations: async (deckId) => {
    return apiRequest(`/api/presentation-hub/decks/${deckId}/publish`, {
      method: 'POST',
    });
  },
  createPresentation: async (payload) => {
    return apiRequest('/api/presentation-hub/presentations', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  updatePresentation: async (presentationId, payload) => {
    return apiRequest(`/api/presentation-hub/presentations/${presentationId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  getPublicPresentationByPath: async (path) => {
    const params = new URLSearchParams({ path });
    const response = await fetch(`${API_BASE}/api/presentation-hub/public/presentations/by-path?${params.toString()}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Presentation route not found' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.json();
  },
  getPortals: async () => {
    return apiRequest('/api/presentation-hub/portals');
  },
  createPortal: async (payload) => {
    return apiRequest('/api/presentation-hub/portals', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  updatePortal: async (portalId, payload) => {
    return apiRequest(`/api/presentation-hub/portals/${portalId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  deletePortal: async (portalId) => {
    return apiRequest(`/api/presentation-hub/portals/${portalId}`, {
      method: 'DELETE',
    });
  },
  getAllPortalActivity: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value);
      }
    });
    const query = params.toString();
    return apiRequest(`/api/presentation-hub/portals/activity${query ? `?${query}` : ''}`);
  },
  getPortalActivity: async (slug, filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value);
      }
    });
    const query = params.toString();
    return apiRequest(`/api/presentation-hub/portals/${slug}/activity${query ? `?${query}` : ''}`);
  },
  getPublicPortal: async (slug) => {
    return apiRequest(`/api/presentation-hub/public/portals/${slug}`);
  },
  trackPublicPortalEvent: async (slug, payload) => {
    return apiRequest(`/api/presentation-hub/public/portals/${slug}/events`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

// WMS Core API
export const wmsAPI = {
  // Scan
  scan: async (scanData) => {
    return apiRequest('/api/wms/scan', {
      method: 'POST',
      body: JSON.stringify(scanData),
    });
  },

  // Inventory
  getInventory: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.sku) params.append('sku', filters.sku);
    if (filters.location) params.append('location', filters.location);
    if (filters.status) params.append('status', filters.status);
    return apiRequest(`/api/wms/inventory?${params}`);
  },
  getDashboardActivity: async () => apiRequest('/api/wms/dashboard/activity'),
  getDashboardThroughput: async () => apiRequest('/api/wms/dashboard/throughput'),

  createInventory: async (data) => {
    return apiRequest('/api/wms/inventory', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Shipments (ASN)
  getShipments: async (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiRequest(`/api/wms/shipments${params}`);
  },

  createShipment: async (data) => {
    return apiRequest('/api/wms/shipments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Orders (legacy)
  getOrders: async (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiRequest(`/api/wms/orders${params}`);
  },

  createOrder: async (data) => {
    return apiRequest('/api/wms/orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Audit & History
  getAuditLogs: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.entity_type) params.append('entity_type', filters.entity_type);
    if (filters.action) params.append('action', filters.action);
    if (filters.limit) params.append('limit', filters.limit);
    return apiRequest(`/api/wms/audit-logs?${params}`);
  },

  getScanHistory: async (workflow = null, limit = 50) => {
    const params = new URLSearchParams();
    if (workflow) params.append('workflow', workflow);
    params.append('limit', limit);
    return apiRequest(`/api/wms/scan-history?${params}`);
  },
};

// OIG (Inbound Gateway) API
export const oigAPI = {
  getStats: async () => apiRequest('/api/oig/stats'),
  getASNList: async () => apiRequest('/api/oig/asn'),
  getASN: async (shipmentId) => apiRequest(`/api/oig/asn/${shipmentId}`),
  intakeNFe: async (data) => apiRequest('/api/oig/intake/nfe', { method: 'POST', body: JSON.stringify(data) }),
  intakeCTe: async (data) => apiRequest('/api/oig/intake/cte', { method: 'POST', body: JSON.stringify(data) }),
  validateNFe: async (accessKey) => apiRequest(`/api/oig/validate/nfe?access_key=${accessKey}`, { method: 'POST' }),
  getDocuments: async (type = null) => apiRequest(`/api/oig/documents${type ? '?doc_type=' + type : ''}`),
  getConfig: async () => apiRequest('/api/oig/nfe-config'),
  getNFeList: async (params = {}) => {
    const query = params.status ? `?status=${params.status}` : '';
    return apiRequest(`/api/oig/nfe-list${query}`);
  },
  getCTeList: async () => apiRequest('/api/oig/cte-list'),
  getCSVTemplates: async () => apiRequest('/api/oig/csv-templates'),
  getImportHistory: async (limit = 20) => apiRequest(`/api/oig/import-history?limit=${limit}`),
  getWeeklyChartData: async () => apiRequest('/api/oig/chart-data/weekly'),
  getDistributionChartData: async () => apiRequest('/api/oig/chart-data/distribution'),
  getDocumentCounts: async () => apiRequest('/api/oig/document-counts'),
  getEDIPartners: async () => apiRequest('/api/oig/edi-partners'),
  getEDIMessages: async (partnerId = null) => apiRequest(`/api/oig/edi-messages${partnerId ? '?partner_id=' + partnerId : ''}`),
};

// Finance API
export const financeAPI = {
  getDashboard: async () => apiRequest('/api/finance/fm/dashboard'),
  getTaxObligations: async (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiRequest(`/api/finance/tax-obligations${params}`);
  },
  getCustomers: async (search = '') => apiRequest(`/api/finance/ar/customers${search ? '?search=' + search : ''}`),
  getInvoices: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.customer_id) params.append('customer_id', filters.customer_id);
    return apiRequest(`/api/finance/ar/invoices?${params}`);
  },
  getVendors: async (search = '') => apiRequest(`/api/finance/ap/vendors${search ? '?search=' + search : ''}`),
  getBills: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.vendor_id) params.append('vendor_id', filters.vendor_id);
    return apiRequest(`/api/finance/ap/bills?${params}`);
  },
  getBankAccounts: async () => apiRequest('/api/finance/bank-accounts'),
  getBankTransactions: async () => apiRequest('/api/finance/bank-transactions'),
  getReconciliationStatus: async () => apiRequest('/api/finance/reconciliation-status'),
  getBudgets: async () => apiRequest('/api/finance/budgets'),
};

// Receiving API
export const receivingAPI = {
  getStats: async () => apiRequest('/api/receiving/stats'),
  getPendingASNs: async () => apiRequest('/api/receiving/pending'),
  startReceiving: async (shipmentId, dock) => apiRequest(`/api/receiving/start/${shipmentId}?dock=${dock}`, { method: 'POST' }),
  receiveItem: async (data) => apiRequest('/api/receiving/receive-item', { method: 'POST', body: JSON.stringify(data) }),
  completeReceiving: async (data) => apiRequest('/api/receiving/complete', { method: 'POST', body: JSON.stringify(data) }),
  getDiscrepancies: async () => apiRequest('/api/receiving/discrepancies'),
  resolveDiscrepancy: async (id, resolution) => apiRequest(`/api/receiving/discrepancies/${id}/resolve`, { method: 'POST', body: JSON.stringify(resolution) }),
  // Put-away
  getPutawayTasks: async (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiRequest(`/api/receiving/putaway/tasks${params}`);
  },
  assignPutaway: async (taskId) => apiRequest(`/api/receiving/putaway/assign/${taskId}`, { method: 'POST' }),
  startPutaway: async (taskId) => apiRequest(`/api/receiving/putaway/start/${taskId}`, { method: 'POST' }),
  confirmPutaway: async (data) => apiRequest('/api/receiving/putaway/confirm', { method: 'POST', body: JSON.stringify(data) }),
  // Locations
  getLocations: async () => apiRequest('/api/receiving/locations'),
  createLocation: async (data) => apiRequest('/api/receiving/locations', { method: 'POST', body: JSON.stringify(data) }),
};

// Sales Orders API
export const salesAPI = {
  getStats: async () => apiRequest('/api/sales/stats'),
  getOrders: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.customer) params.append('customer', filters.customer);
    if (filters.priority) params.append('priority', filters.priority);
    return apiRequest(`/api/sales/orders?${params}`);
  },
  getOrder: async (orderId) => apiRequest(`/api/sales/orders/${orderId}`),
  createOrder: async (data) => apiRequest('/api/sales/orders', { method: 'POST', body: JSON.stringify(data) }),
  allocateOrder: async (orderId) => apiRequest(`/api/sales/orders/${orderId}/allocate`, { method: 'POST' }),
  deallocateOrder: async (orderId) => apiRequest(`/api/sales/orders/${orderId}/deallocate`, { method: 'POST' }),
  releaseToPickOrder: async (orderId) => apiRequest(`/api/sales/orders/${orderId}/release`, { method: 'POST' }),
  cancelOrder: async (orderId, reason) => apiRequest(`/api/sales/orders/${orderId}/cancel?reason=${encodeURIComponent(reason)}`, { method: 'POST' }),
  createWave: async (data) => apiRequest('/api/sales/waves/create', { method: 'POST', body: JSON.stringify(data) }),
};

// Picking API
export const pickingAPI = {
  getStats: async () => apiRequest('/api/picking/stats'),
  getTasks: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    return apiRequest(`/api/picking/tasks?${params}`);
  },
  startPicking: async (orderId) => apiRequest('/api/picking/start', { method: 'POST', body: JSON.stringify({ order_id: orderId }) }),
  verifyLocation: async (orderId, location) => apiRequest('/api/picking/verify-location', { method: 'POST', body: JSON.stringify({ order_id: orderId, location }) }),
  confirmPick: async (data) => apiRequest('/api/picking/confirm-pick', { method: 'POST', body: JSON.stringify(data) }),
  shortPick: async (data) => apiRequest('/api/picking/short-pick', { method: 'POST', body: JSON.stringify(data) }),
  completePicking: async (orderId, force = false) => apiRequest(`/api/picking/complete/${orderId}?force=${force}`, { method: 'POST' }),
};

// Packing API
export const packingAPI = {
  getStats: async () => apiRequest('/api/packing/stats'),
  getQueue: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.priority) params.append('priority', filters.priority);
    return apiRequest(`/api/packing/queue?${params}`);
  },
  startPacking: async (orderId, stationId = 'PACK-01') => apiRequest('/api/packing/start', { method: 'POST', body: JSON.stringify({ order_id: orderId, station_id: stationId }) }),
  verifyItem: async (data) => apiRequest('/api/packing/verify-item', { method: 'POST', body: JSON.stringify(data) }),
  createCarton: async (orderId, cartonType = 'standard') => apiRequest('/api/packing/cartons/create', { method: 'POST', body: JSON.stringify({ order_id: orderId, carton_type: cartonType }) }),
  sealCarton: async (data) => apiRequest('/api/packing/cartons/seal', { method: 'POST', body: JSON.stringify(data) }),
  getCarton: async (cartonId) => apiRequest(`/api/packing/cartons/${cartonId}`),
  generateLabel: async (cartonId, labelType = 'shipping') => apiRequest('/api/packing/labels/generate', { method: 'POST', body: JSON.stringify({ carton_id: cartonId, label_type: labelType }) }),
  previewLabel: async (cartonId) => apiRequest(`/api/packing/labels/preview/${cartonId}`),
  completePacking: async (orderId) => apiRequest(`/api/packing/complete/${orderId}`, { method: 'POST' }),
};

// Pallet Builder API
export const palletAPI = {
  getStats: async () => apiRequest('/api/pallets/stats/summary'),
  getPallets: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.carrier) params.append('carrier', filters.carrier);
    return apiRequest(`/api/pallets/?${params}`);
  },
  getPallet: async (palletId) => apiRequest(`/api/pallets/${palletId}`),
  createPallet: async (data) => apiRequest('/api/pallets/create', { method: 'POST', body: JSON.stringify(data) }),
  addCarton: async (palletId, cartonId) => apiRequest('/api/pallets/add-carton', { method: 'POST', body: JSON.stringify({ pallet_id: palletId, carton_id: cartonId }) }),
  removeCarton: async (palletId, cartonId) => apiRequest('/api/pallets/remove-carton', { method: 'POST', body: JSON.stringify({ pallet_id: palletId, carton_id: cartonId }) }),
  sealPallet: async (data) => apiRequest('/api/pallets/seal', { method: 'POST', body: JSON.stringify(data) }),
  stagePallet: async (palletId, location) => apiRequest('/api/pallets/stage', { method: 'POST', body: JSON.stringify({ pallet_id: palletId, staging_location: location }) }),
  generateLabel: async (palletId) => apiRequest(`/api/pallets/labels/${palletId}`, { method: 'POST' }),
};

// Dispatch/Expedition API
export const dispatchAPI = {
  getStats: async () => apiRequest('/api/dispatch/stats/summary'),
  getDispatches: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.carrier) params.append('carrier', filters.carrier);
    return apiRequest(`/api/dispatch/?${params}`);
  },
  getDispatch: async (dispatchId) => apiRequest(`/api/dispatch/${dispatchId}`),
  scheduleDispatch: async (data) => apiRequest('/api/dispatch/schedule', { method: 'POST', body: JSON.stringify(data) }),
  startLoading: async (dispatchId, dock) => apiRequest(`/api/dispatch/start-loading/${dispatchId}?dock=${dock}`, { method: 'POST' }),
  loadItem: async (dispatchId, itemId) => apiRequest('/api/dispatch/load-item', { method: 'POST', body: JSON.stringify({ dispatch_id: dispatchId, item_id: itemId }) }),
  unloadItem: async (dispatchId, itemId) => apiRequest('/api/dispatch/unload-item', { method: 'POST', body: JSON.stringify({ dispatch_id: dispatchId, item_id: itemId }) }),
  generateBOL: async (dispatchId, includeMdfe = true) => apiRequest('/api/dispatch/generate-bol', { method: 'POST', body: JSON.stringify({ dispatch_id: dispatchId, include_mdfe: includeMdfe }) }),
  confirmDeparture: async (data) => apiRequest('/api/dispatch/confirm-departure', { method: 'POST', body: JSON.stringify(data) }),
  getDockStatus: async () => apiRequest('/api/dispatch/docks/status'),
};

// Delivery/Last-Mile API
export const deliveryAPI = {
  getStats: async () => apiRequest('/api/delivery/stats/summary'),
  getRoutes: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.zone) params.append('zone', filters.zone);
    return apiRequest(`/api/delivery/routes?${params}`);
  },
  getRoute: async (routeId) => apiRequest(`/api/delivery/routes/${routeId}`),
  createRoute: async (data) => apiRequest('/api/delivery/routes/create', { method: 'POST', body: JSON.stringify(data) }),
  assignDriver: async (data) => apiRequest('/api/delivery/routes/assign-driver', { method: 'POST', body: JSON.stringify(data) }),
  startRoute: async (routeId) => apiRequest('/api/delivery/routes/start', { method: 'POST', body: JSON.stringify({ route_id: routeId }) }),
  updateStopStatus: async (data) => apiRequest('/api/delivery/stops/update-status', { method: 'POST', body: JSON.stringify(data) }),
  capturePOD: async (data) => apiRequest('/api/delivery/stops/capture-pod', { method: 'POST', body: JSON.stringify(data) }),
  reportException: async (data) => apiRequest('/api/delivery/stops/report-exception', { method: 'POST', body: JSON.stringify(data) }),
  getStop: async (stopId) => apiRequest(`/api/delivery/stops/${stopId}`),
};

// RMA (Customer Returns) API
export const rmaAPI = {
  getStats: async () => apiRequest('/api/rma/stats/summary'),
  getRMAs: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.customer) params.append('customer', filters.customer);
    return apiRequest(`/api/rma/?${params}`);
  },
  getRMA: async (rmaNumber) => apiRequest(`/api/rma/${rmaNumber}`),
  createRMA: async (data) => apiRequest('/api/rma/create', { method: 'POST', body: JSON.stringify(data) }),
  approveRMA: async (rmaNumber, approved = true, notes = '') => apiRequest('/api/rma/approve', { method: 'POST', body: JSON.stringify({ rma_number: rmaNumber, approved, notes }) }),
  sendReturnLabel: async (rmaNumber) => apiRequest(`/api/rma/send-label/${rmaNumber}`, { method: 'POST' }),
  receiveItem: async (data) => apiRequest('/api/rma/receive-item', { method: 'POST', body: JSON.stringify(data) }),
  inspectItem: async (data) => apiRequest('/api/rma/inspect', { method: 'POST', body: JSON.stringify(data) }),
  resolveRMA: async (data) => apiRequest('/api/rma/resolve', { method: 'POST', body: JSON.stringify(data) }),
  closeRMA: async (rmaNumber) => apiRequest(`/api/rma/close/${rmaNumber}`, { method: 'POST' }),
};

// RTV (Return to Vendor) API
export const rtvAPI = {
  getStats: async () => apiRequest('/api/rtv/stats/summary'),
  getRTVs: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.vendor) params.append('vendor', filters.vendor);
    return apiRequest(`/api/rtv/?${params}`);
  },
  getRTV: async (rtvNumber) => apiRequest(`/api/rtv/${rtvNumber}`),
  createRTV: async (data) => apiRequest('/api/rtv/create', { method: 'POST', body: JSON.stringify(data) }),
  submitRTV: async (rtvNumber) => apiRequest(`/api/rtv/submit/${rtvNumber}`, { method: 'POST' }),
  approveRTV: async (data) => apiRequest('/api/rtv/approve', { method: 'POST', body: JSON.stringify(data) }),
  startPacking: async (rtvNumber) => apiRequest(`/api/rtv/start-packing/${rtvNumber}`, { method: 'POST' }),
  shipRTV: async (data) => apiRequest('/api/rtv/ship', { method: 'POST', body: JSON.stringify(data) }),
  markVendorReceived: async (rtvNumber) => apiRequest(`/api/rtv/vendor-received/${rtvNumber}`, { method: 'POST' }),
  recordCredit: async (data) => apiRequest('/api/rtv/record-credit', { method: 'POST', body: JSON.stringify(data) }),
  closeRTV: async (rtvNumber) => apiRequest(`/api/rtv/close/${rtvNumber}`, { method: 'POST' }),
};

// Transfer Orders API
export const transferAPI = {
  getStats: async () => apiRequest('/api/transfers/stats/summary'),
  getTransfers: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.source) params.append('source', filters.source);
    if (filters.destination) params.append('destination', filters.destination);
    return apiRequest(`/api/transfers/?${params}`);
  },
  getTransfer: async (transferNumber) => apiRequest(`/api/transfers/${transferNumber}`),
  createTransfer: async (data) => apiRequest('/api/transfers/create', { method: 'POST', body: JSON.stringify(data) }),
  submitTransfer: async (transferNumber) => apiRequest(`/api/transfers/submit/${transferNumber}`, { method: 'POST' }),
  approveTransfer: async (transferNumber, approved = true) => apiRequest(`/api/transfers/approve/${transferNumber}?approved=${approved}`, { method: 'POST' }),
  startPicking: async (transferNumber) => apiRequest(`/api/transfers/start-picking/${transferNumber}`, { method: 'POST' }),
  pickItem: async (data) => apiRequest('/api/transfers/pick-item', { method: 'POST', body: JSON.stringify(data) }),
  shipTransfer: async (data) => apiRequest('/api/transfers/ship', { method: 'POST', body: JSON.stringify(data) }),
  startReceiving: async (transferNumber) => apiRequest(`/api/transfers/start-receiving/${transferNumber}`, { method: 'POST' }),
  receiveItem: async (data) => apiRequest('/api/transfers/receive-item', { method: 'POST', body: JSON.stringify(data) }),
  completeTransfer: async (transferNumber, force = false) => apiRequest(`/api/transfers/complete/${transferNumber}?force=${force}`, { method: 'POST' }),
};

// Special Orders API
export const specialOrdersAPI = {
  getStats: async () => apiRequest('/api/special-orders/stats/summary'),
  getSpecialOrders: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.order_type) params.append('order_type', filters.order_type);
    if (filters.status) params.append('status', filters.status);
    return apiRequest(`/api/special-orders/?${params}`);
  },
  getSpecialOrder: async (orderNumber) => apiRequest(`/api/special-orders/${orderNumber}`),
  createDropship: async (data) => apiRequest('/api/special-orders/dropship/create', { method: 'POST', body: JSON.stringify(data) }),
  createConsignment: async (data) => apiRequest('/api/special-orders/consignment/create', { method: 'POST', body: JSON.stringify(data) }),
  recordConsignmentSale: async (data) => apiRequest('/api/special-orders/consignment/record-sale', { method: 'POST', body: JSON.stringify(data) }),
  createRushOrder: async (data) => apiRequest('/api/special-orders/rush/create', { method: 'POST', body: JSON.stringify(data) }),
  createBackorder: async (data) => apiRequest('/api/special-orders/backorder/create', { method: 'POST', body: JSON.stringify(data) }),
  updateStatus: async (data) => apiRequest('/api/special-orders/update-status', { method: 'POST', body: JSON.stringify(data) }),
};

// Procurement API
export const procurementAPI = {
  getDashboard: async () => apiRequest('/api/procurement/dashboard'),
  // Purchase Requisitions
  getRequisitions: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    return apiRequest(`/api/procurement/requisitions?${params}`);
  },
  getRequisition: async (prId) => apiRequest(`/api/procurement/requisitions/${prId}`),
  createRequisition: async (data) => apiRequest('/api/procurement/requisitions', { method: 'POST', body: JSON.stringify(data) }),
  approveRequisition: async (prId) => apiRequest(`/api/procurement/requisitions/${prId}/approve`, { method: 'POST' }),
  convertToPO: async (prId) => apiRequest(`/api/procurement/requisitions/${prId}/convert`, { method: 'POST' }),
  // Purchase Orders
  getOrders: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    return apiRequest(`/api/procurement/orders?${params}`);
  },
  getOrder: async (poId) => apiRequest(`/api/procurement/orders/${poId}`),
  updateOrderStatus: async (poId, status) => apiRequest(`/api/procurement/orders/${poId}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  createASN: async (poId, carrier) => apiRequest(`/api/procurement/orders/${poId}/create-asn`, { method: 'POST', body: JSON.stringify({ carrier }) }),
};

// WebSocket connection for real-time updates
export class WMSWebSocket {
  constructor(channel = 'wms') {
    this.channel = channel;
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  connect() {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = API_BASE.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}//${wsHost}/ws/${this.channel}`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log(`WebSocket connected to ${this.channel}`);
        this.reconnectAttempts = 0;
        this.emit('connected', { channel: this.channel });
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.emit(data.type, data);
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.emit('disconnected', {});
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.emit('error', { error });
      };
    } catch (e) {
      console.error('WebSocket connection failed:', e);
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Reconnecting... attempt ${this.reconnectAttempts}`);
      setTimeout(() => this.connect(), 2000 * this.reconnectAttempts);
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(cb => cb(data));
    }
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  ping() {
    this.send({ type: 'ping' });
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}

// Products API
export const productsAPI = {
  getProducts: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.category) params.append('category', filters.category);
    const queryString = params.toString();
    return apiRequest(`/api/products/${queryString ? '?' + queryString : ''}`);
  },
  getStats: async () => apiRequest('/api/products/stats'),
  getProduct: async (sku) => apiRequest(`/api/products/${sku}`),
  createProduct: async (data) => apiRequest('/api/products', { method: 'POST', body: JSON.stringify(data) }),
  updateProduct: async (sku, data) => apiRequest(`/api/products/${sku}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProduct: async (sku) => apiRequest(`/api/products/${sku}`, { method: 'DELETE' }),
};

// Warehouse Configuration API
export const warehouseAPI = {
  getConfig: async () => apiRequest('/api/warehouse/config'),
  // Zones
  getZones: async () => apiRequest('/api/warehouse/zones'),
  getZone: async (zoneId) => apiRequest(`/api/warehouse/zones/${zoneId}`),
  createZone: async (data) => apiRequest('/api/warehouse/zones', { method: 'POST', body: JSON.stringify(data) }),
  updateZone: async (zoneId, data) => apiRequest(`/api/warehouse/zones/${zoneId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteZone: async (zoneId) => apiRequest(`/api/warehouse/zones/${zoneId}`, { method: 'DELETE' }),
  // Aisles
  getAisles: async (zoneId = null) => {
    const params = zoneId ? `?zone_id=${zoneId}` : '';
    return apiRequest(`/api/warehouse/aisles${params}`);
  },
  createAisle: async (data) => apiRequest('/api/warehouse/aisles', { method: 'POST', body: JSON.stringify(data) }),
  deleteAisle: async (aisleId) => apiRequest(`/api/warehouse/aisles/${aisleId}`, { method: 'DELETE' }),
  // Bin Types
  getBinTypes: async () => apiRequest('/api/warehouse/bin-types'),
  createBinType: async (data) => apiRequest('/api/warehouse/bin-types', { method: 'POST', body: JSON.stringify(data) }),
  deleteBinType: async (binTypeId) => apiRequest(`/api/warehouse/bin-types/${binTypeId}`, { method: 'DELETE' }),
};

// Inventory Adjustments API
export const adjustmentsAPI = {
  getAdjustments: async (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiRequest(`/api/adjustments/${params}`);
  },
  getStats: async () => apiRequest('/api/adjustments/stats'),
  getReasons: async () => apiRequest('/api/adjustments/reasons'),
  getAdjustment: async (id) => apiRequest(`/api/adjustments/${id}`),
  createAdjustment: async (data) => apiRequest('/api/adjustments/', { method: 'POST', body: JSON.stringify(data) }),
  approveAdjustment: async (id, approved, notes = null) =>
    apiRequest(`/api/adjustments/${id}/approve`, { method: 'POST', body: JSON.stringify({ approved, notes }) }),
  batchApprove: async (ids) =>
    apiRequest('/api/adjustments/batch-approve', { method: 'POST', body: JSON.stringify(ids) }),
  // Cycle Counts
  getCycleCounts: async (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiRequest(`/api/adjustments/cycle-counts/${params}`);
  },
  createCycleCount: async (data) => apiRequest('/api/adjustments/cycle-counts/', { method: 'POST', body: JSON.stringify(data) }),
  startCycleCount: async (id) => apiRequest(`/api/adjustments/cycle-counts/${id}/start`, { method: 'POST' }),
  completeCycleCount: async (id, variance) =>
    apiRequest(`/api/adjustments/cycle-counts/${id}/complete?variance=${variance}`, { method: 'POST' }),
  // Audit Log
  getAuditLog: async (limit = 50) => apiRequest(`/api/adjustments/audit-log/?limit=${limit}`),
};

// Trails / Event Bus API
export const trailsAPI = {
  getEvents: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.entity_type) params.append('entity_type', filters.entity_type);
    if (filters.entity_id) params.append('entity_id', filters.entity_id);
    if (filters.trail_type) params.append('trail_type', filters.trail_type);
    return apiRequest(`/api/trails/events?${params}`);
  },
  validateScan: async (data) => {
    const params = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value);
      }
    });
    return apiRequest(`/api/trails/scan/validate?${params}`, { method: 'POST' });
  },
  getScanHistory: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.gate_id) query.append('gate_id', params.gate_id);
    if (params.operator_id) query.append('operator_id', params.operator_id);
    return apiRequest(`/api/trails/scan/history?${query}`);
  },
};

// Reports & Analytics API
export const reportsAPI = {
  getThroughput: async (period = '7d') => apiRequest(`/api/reports/throughput?period=${period}`),
  getLaborProductivity: async (period = '7d') => apiRequest(`/api/reports/labor-productivity?period=${period}`),
  getSpaceUtilization: async () => apiRequest('/api/reports/space-utilization'),
  getOrderAccuracy: async () => apiRequest('/api/reports/order-accuracy'),
  getRealtime: async () => apiRequest('/api/reports/realtime'),
  getAlerts: async (limit = 10, severity = null) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (severity) params.append('severity', severity);
    return apiRequest(`/api/reports/alerts?${params}`);
  },
  acknowledgeAlert: async (alertId) => apiRequest(`/api/reports/alerts/${alertId}/acknowledge`, { method: 'POST' }),
  exportReport: async (reportType, format = 'csv') =>
    apiRequest(`/api/reports/export?report_type=${reportType}&format=${format}`),
};

// Device Management API
export const devicesAPI = {
  // Device CRUD
  getDevices: async (deviceType = null, status = null) => {
    const params = new URLSearchParams();
    if (deviceType) params.append('device_type', deviceType);
    if (status) params.append('status', status);
    return apiRequest(`/api/devices/?${params}`);
  },
  getDevice: async (deviceId) => apiRequest(`/api/devices/${deviceId}`),
  getStats: async () => apiRequest('/api/devices/stats'),
  getModels: async (deviceType = null) => {
    const params = deviceType ? `?device_type=${deviceType}` : '';
    return apiRequest(`/api/devices/models${params}`);
  },
  createDevice: async (data) => apiRequest('/api/devices/', { method: 'POST', body: JSON.stringify(data) }),
  updateDevice: async (deviceId, data) => apiRequest(`/api/devices/${deviceId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDevice: async (deviceId) => apiRequest(`/api/devices/${deviceId}`, { method: 'DELETE' }),
  testConnection: async (deviceId) => apiRequest(`/api/devices/${deviceId}/test-connection`, { method: 'POST' }),
  heartbeat: async (deviceId) => apiRequest(`/api/devices/${deviceId}/heartbeat`, { method: 'POST' }),

  // Mobile device registration
  registerMobile: async (deviceId, userAgent, deviceName = null) => {
    const params = new URLSearchParams({ device_id: deviceId, user_agent: userAgent });
    if (deviceName) params.append('device_name', deviceName);
    return apiRequest(`/api/devices/mobile/register?${params}`, { method: 'POST' });
  },

  // Printing
  getAvailablePrinters: async () => apiRequest('/api/devices/printers/available'),
  getLabelTemplates: async () => apiRequest('/api/devices/templates'),
  submitPrintJob: async (printerId, labelType, content, copies = 1) =>
    apiRequest('/api/devices/print', {
      method: 'POST',
      body: JSON.stringify({ printer_id: printerId, label_type: labelType, content, copies })
    }),
  getPrintQueue: async (printerId = null, limit = 50) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (printerId) params.append('printer_id', printerId);
    return apiRequest(`/api/devices/print/queue?${params}`);
  },
  previewLabel: async (labelType, content) =>
    apiRequest('/api/devices/print/preview', {
      method: 'POST',
      body: JSON.stringify({ label_type: labelType, content })
    }),
};

// Tenancy & Context API (Phase 2)
export const tenancyAPI = {
  getAvailableScopes: async () => apiRequest('/api/tenancy/available-scopes'),
  getCurrentContext: async () => apiRequest('/api/tenancy/current-context'),
};

export default {
  auth: authAPI,
  wms: wmsAPI,
  oig: oigAPI,
  receiving: receivingAPI,
  sales: salesAPI,
  picking: pickingAPI,
  packing: packingAPI,
  pallets: palletAPI,
  dispatch: dispatchAPI,
  delivery: deliveryAPI,
  rma: rmaAPI,
  rtv: rtvAPI,
  transfers: transferAPI,
  specialOrders: specialOrdersAPI,
  procurement: procurementAPI,
  products: productsAPI,
  warehouse: warehouseAPI,
  adjustments: adjustmentsAPI,
  reports: reportsAPI,
  devices: devicesAPI,
  trails: trailsAPI,
  tenancy: tenancyAPI,
  WMSWebSocket,
  isAuthenticated,
  getCurrentUser,
  logout,
};
